import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle, XCircle, AlertCircle, Upload, Eye, Trash2, ArrowRight, Pencil, Save, X, Database } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface WorksheetScannerProps {
  county: string;
  onRecordsUpdated?: () => void;
  onViewUpdatedRecords?: () => void;
}

interface ScanResult {
  doc: string;
  dbDoc?: string;
  dbId?: string;
  name?: string;
  dbName?: string;
  address?: string;
  existingAddress?: string | null;
  col5?: string;
  matchMethod?: string;
  filingDate?: string;
  status: 'matched' | 'skipped' | 'not_found' | 'error' | 'saved';
  reason?: string;
}

export function WorksheetScanner({ county, onRecordsUpdated, onViewUpdatedRecords }: WorksheetScannerProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [results, setResults] = useState<{ matched: number; skipped: number; notFound: number; details: ScanResult[] } | null>(null);
  const [totalSaved, setTotalSaved] = useState(0);
  const [sheetsScanned, setSheetsScanned] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [integrated, setIntegrated] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setImageData(event.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => setImageData(event.target?.result as string);
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleScan = async () => {
    if (!imageData) return;
    setIsProcessing(true);
    setResults(null);
    setIntegrated(false);

    try {
      const { data, error } = await supabase.functions.invoke("scan-address-worksheet", {
        body: { imageData, county },
      });

      if (error) throw error;

      if (data.success) {
        setResults(data.results);
        setSheetsScanned(prev => prev + 1);
        const matchCount = data.results.matched || 0;
        toast.success(`Sheet ${sheetsScanned + 1}: Found ${matchCount} matches — review and click "Save to Database" to commit`);
      } else {
        throw new Error(data.error || "Scan failed");
      }
    } catch (error: any) {
      console.error("Scan error:", error);
      toast.error(error.message || "Failed to scan worksheet");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIntegrate = async () => {
    if (!results) return;

    const recordsToSave = results.details
      .filter(r => r.status === 'matched' && r.dbId && r.address)
      .map(r => ({ dbId: r.dbId!, address: r.address! }));

    if (recordsToSave.length === 0) {
      toast.error("No matched records with addresses to save");
      return;
    }

    setIsSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("scan-address-worksheet", {
        body: { integrate: true, records: recordsToSave },
      });

      if (error) throw error;

      if (data.success) {
        setTotalSaved(prev => prev + data.saved);
        setIntegrated(true);

        // Update local state to show saved status
        const updatedDetails = results.details.map(r => 
          r.status === 'matched' && r.dbId && r.address ? { ...r, status: 'saved' as const } : r
        );
        setResults({ ...results, details: updatedDetails });

        toast.success(`✅ Saved ${data.saved} addresses to database`);
        onRecordsUpdated?.();
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save to database");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearResults = () => {
    setResults(null);
    setImageData(null);
    setIntegrated(false);
    setEditingIndex(null);
    toast.info("Scan results cleared (database not affected)");
  };

  const handleNextSheet = () => {
    setImageData(null);
    setResults(null);
    setIntegrated(false);
    setEditingIndex(null);
  };

  const startEdit = (index: number, currentAddress: string | undefined) => {
    setEditingIndex(index);
    setEditValue(currentAddress || "");
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  const saveEdit = async (index: number) => {
    if (!results) return;
    const row = results.details[index];

    // If already integrated, save directly to DB
    if (integrated && row.dbId) {
      setSavingIndex(index);
      const newAddress = editValue.trim() || null;
      try {
        const { error } = await supabase
          .from('county_research')
          .update({
            grantor_address: newAddress,
            address_extraction_method: newAddress ? 'worksheet_scan_manual' : null,
            address_needs_review: false,
          })
          .eq('id', row.dbId);
        if (error) throw error;
        
        const updatedDetails = [...results.details];
        updatedDetails[index] = { ...updatedDetails[index], address: newAddress || undefined, status: newAddress ? 'saved' : 'skipped' };
        setResults({ ...results, details: updatedDetails });
        setEditingIndex(null);
        setEditValue("");
        toast.success(`Address updated in database`);
        onRecordsUpdated?.();
      } catch (error: any) {
        toast.error(error.message || "Failed to save");
      } finally {
        setSavingIndex(null);
      }
      return;
    }

    // Pre-integration: just update local state
    const updatedDetails = [...results.details];
    const newAddress = editValue.trim() || null;
    updatedDetails[index] = {
      ...updatedDetails[index],
      address: newAddress || undefined,
      status: newAddress ? 'matched' : 'skipped',
    };
    const matched = updatedDetails.filter(d => d.status === 'matched').length;
    const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
    setResults({ ...results, details: updatedDetails, matched, skipped });
    setEditingIndex(null);
    setEditValue("");
    toast.success("Address updated locally — click 'Save to Database' to commit");
  };

  // For not_found rows, allow manual DB lookup + edit
  const saveEditWithLookup = async (index: number) => {
    if (!results) return;
    const row = results.details[index];
    const newAddress = editValue.trim() || null;
    let dbId = row.dbId;

    if (!dbId && row.dbName) {
      const { data } = await supabase
        .from('county_research')
        .select('id')
        .ilike('grantor_name', row.dbName)
        .eq('county', county.toLowerCase())
        .is('deleted_at', null)
        .limit(1);
      if (data && data.length > 0) dbId = data[0].id;
    }
    if (!dbId && row.name) {
      let query = supabase
        .from('county_research')
        .select('id')
        .ilike('grantor_name', row.name)
        .eq('county', county.toLowerCase())
        .is('deleted_at', null);
      if (row.doc && row.doc.match(/^\d{4}-\d{2}-\d{2}$/)) {
        query = query.eq('filing_date', row.doc);
      }
      const { data } = await query.limit(1);
      if (data && data.length > 0) dbId = data[0].id;
    }

    // If no DB record found, just update local state instead of erroring
    if (!dbId) {
      const updatedDetails = [...results.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        address: newAddress || undefined,
        status: newAddress ? 'matched' : 'skipped',
      };
      const matched = updatedDetails.filter(d => d.status === 'matched').length;
      const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
      setResults({ ...results, details: updatedDetails, matched, skipped });
      setEditingIndex(null);
      setEditValue("");
      toast.success("Updated locally (no DB record found to save to)");
      return;
    }

    setSavingIndex(index);

    try {
      const { error } = await supabase
        .from('county_research')
        .update({
          grantor_address: newAddress,
          address_extraction_method: newAddress ? 'worksheet_scan_manual' : null,
          address_needs_review: false,
        })
        .eq('id', dbId);

      if (error) throw error;

      const updatedDetails = [...results.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        dbId,
        address: newAddress || undefined,
        status: newAddress ? 'saved' : 'skipped',
      };
      setResults({ ...results, details: updatedDetails });
      setEditingIndex(null);
      setEditValue("");
      toast.success(`Address saved for ${row.name || row.dbName}`);
      onRecordsUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setSavingIndex(null);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = results?.details[index];
      if (row?.status === 'not_found' || (integrated && row?.dbId)) {
        saveEditWithLookup(index);
      } else {
        saveEdit(index);
      }
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'matched': return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'saved': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'skipped': return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
      case 'not_found': return <XCircle className="h-4 w-4 text-yellow-600" />;
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
      default: return null;
    }
  };

  const matchedWithAddresses = results?.details.filter(r => r.status === 'matched' && r.address).length || 0;

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Scan Handwritten Address Worksheet
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Upload each worksheet photo. Review the extracted addresses, then click <strong>"Save to Database"</strong> to commit.
          </p>
        </div>
        {results && !integrated && (
          <Button variant="outline" size="sm" onClick={handleClearResults} className="text-muted-foreground shrink-0">
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Results
          </Button>
        )}
      </div>

      {sheetsScanned > 0 && (
        <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-4 text-sm">
          <span className="font-medium">Session Progress:</span>
          <Badge variant="secondary">{sheetsScanned} sheet{sheetsScanned !== 1 ? 's' : ''} scanned</Badge>
          <Badge variant="default" className="bg-green-600">{totalSaved} total saved to DB</Badge>
        </div>
      )}

      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
        onPaste={handlePaste}
        tabIndex={0}
      >
        {!imageData ? (
          <div className="space-y-3">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">
              {sheetsScanned > 0
                ? `Ready for sheet ${sheetsScanned + 1} — upload or paste the next worksheet photo`
                : "Upload a photo of the worksheet or paste from clipboard"}
            </p>
            <div className="flex gap-2 justify-center">
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span><Upload className="mr-2 h-4 w-4" />Upload Photo</span>
                </Button>
              </label>
              <label>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span>📁 Choose File</span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Or click here and press Ctrl+V to paste</p>
          </div>
        ) : (
          <div className="space-y-4">
            <img src={imageData} alt="Worksheet photo" className="max-h-96 mx-auto rounded border" />
            <div className="flex gap-2 justify-center">
              {!results ? (
                <>
                  <Button onClick={handleScan} disabled={isProcessing} size="lg">
                    {isProcessing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading Handwriting...</>
                    ) : (
                      <><CheckCircle className="mr-2 h-4 w-4" />Scan & Preview</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setImageData(null)} disabled={isProcessing}>
                    Clear
                  </Button>
                </>
              ) : (
                <>
                  {!integrated && matchedWithAddresses > 0 && (
                    <Button onClick={handleIntegrate} disabled={isSaving} size="lg" className="bg-green-600 hover:bg-green-700">
                      {isSaving ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        <><Database className="mr-2 h-4 w-4" />Save {matchedWithAddresses} to Database</>
                      )}
                    </Button>
                  )}
                  <Button onClick={handleNextSheet} size="lg" variant={integrated ? "default" : "outline"}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Scan Next Sheet
                  </Button>
                  {onViewUpdatedRecords && integrated && (
                    <Button variant="outline" onClick={onViewUpdatedRecords}>
                      <Eye className="mr-2 h-4 w-4" />
                      View Updated Records
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {results && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            {!integrated && results.matched > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                {results.matched} Ready to Save
              </Badge>
            )}
            {integrated && (
              <Badge variant="default" className="bg-green-600">
                ✅ {results.details.filter(d => d.status === 'saved').length} Saved
              </Badge>
            )}
            <Badge variant="secondary">{results.skipped} Blank</Badge>
            {results.notFound > 0 && <Badge variant="outline" className="text-yellow-600">{results.notFound} Not Found</Badge>}
            <span className="text-xs text-muted-foreground ml-2">
              {integrated ? "Saved to database. Edit with pencil icon." : "⚠️ Not yet saved — review and click Save to Database"}
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
              <tr>
                  <th className="text-left p-2 w-10">Status</th>
                  <th className="text-left p-2 w-24">Date</th>
                  <th className="text-left p-2 w-24">Doc #</th>
                  <th className="text-left p-2">Seller</th>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2 w-28">Match</th>
                  <th className="text-left p-2 w-16">Edit</th>
                </tr>
              </thead>
              <tbody>
                {results.details.map((r, i) => (
                  <tr key={i} className={`border-t ${r.status === 'matched' && !integrated ? 'bg-amber-50' : ''}`}>
                    <td className="p-2">{statusIcon(r.status)}</td>
                    <td className="p-2 font-mono text-xs">{r.filingDate || '-'}</td>
                    <td className="p-2 font-mono text-xs">{r.doc}</td>
                    <td className="p-2">{r.name || '-'}</td>
                    <td className="p-2">
                      {editingIndex === i ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, i)}
                            className="h-7 text-sm"
                            placeholder="Enter address or leave blank to clear"
                            autoFocus
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 shrink-0"
                            onClick={() => {
                              const row = results.details[i];
                              if (row.status === 'not_found' || (integrated && row.dbId)) {
                                saveEditWithLookup(i);
                              } else {
                                saveEdit(i);
                              }
                            }}
                            disabled={savingIndex === i}
                          >
                            {savingIndex === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 text-green-600" />}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={cancelEdit}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        r.address || <span className="text-muted-foreground italic">{r.col5 || r.reason}</span>
                      )}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">{r.matchMethod || '-'}</td>
                    <td className="p-2">
                      {editingIndex !== i && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(i, r.address)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => {
                            const updatedDetails = results!.details.filter((_, idx) => idx !== i);
                            const matched = updatedDetails.filter(d => d.status === 'matched').length;
                            const skipped = updatedDetails.filter(d => d.status === 'skipped').length;
                            const notFound = updatedDetails.filter(d => d.status === 'not_found').length;
                            setResults({ matched, skipped, notFound, details: updatedDetails });
                          }}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
